"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { Bookmark, BookmarkCheck, Heart, Lock, LogIn, PlayCircle } from "lucide-react";
import { cn } from "@/components/ui/cn";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { formatPrice } from "@/lib/format";
import { enroll, likeModule, saveModule } from "@/lib/lecturehub/module-actions";
import { ShareActions } from "@/components/ui/ShareActions";
import type { SharePayload } from "./moduleShare";
import { paidLockKind } from "./paywall";

type Row = {
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  label: string;
  value: string;
};

type Props = {
  lang: Lang;
  moduleId: number;
  free: boolean;
  price: number | null;
  canView: boolean;
  isAuthed: boolean;
  /** Portal runs in subscription mode (`portal.paymentModel`). */
  isSubscription: boolean;
  initialLiked: boolean;
  initialSaved: boolean;
  /** Anchor href that smooth-scrolls to the player on the page. */
  playAnchor?: string;
  /** null/absent → no share (PRIVATE portal, or caller opted out). */
  share?: SharePayload | null;
  /** "Watch now" / "Listen now" caption. */
  playLabel?: string;
  /** Free-enrol CTA caption, per media type (e.g. "Klikni in poslušaj" for
   *  podcasts, "Klikni in glej" for OTT). The free badge already signals €0. */
  freeLabel?: string;
  /** Subscribe label override. */
  subscribeLabel?: string;
  /**
   * When true, suppress the "play" CTA even though the user can view.
   * Use this when the actual media player is already rendered visibly
   * elsewhere on the page (e.g. PodcastView puts the player in the
   * About card), so the sidebar CTA would be a redundant anchor link.
   * The subscribe CTA is still shown when canView is false.
   */
  hidePlayCta?: boolean;
  /** Show the "Save" action alongside "Favorites". Default true; OTT detail
   *  passes false so it shows Favorites only (matches CourseStartCard). */
  showSave?: boolean;
  rows?: Row[];
};

export function MediaSidebar({
  lang,
  moduleId,
  free,
  price,
  canView,
  isAuthed,
  isSubscription,
  initialLiked,
  initialSaved,
  playAnchor = "#player",
  share = null,
  playLabel,
  freeLabel,
  subscribeLabel,
  hidePlayCta = false,
  showSave = true,
  rows = [],
}: Props) {
  const t = useDict();
  const pt = t.portal;
  const router = useRouter();
  const [liked, setLiked] = useState(initialLiked);
  const [saved, setSaved] = useState(initialSaved);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function loginUrl(): string {
    return `${langPath(lang, "/login")}?redirect=${encodeURIComponent(window.location.pathname)}`;
  }

  // Free OTT/podcasts are "enrol for €0": access still requires an enrolment
  // (single-purchase portals gate ALL content behind it — see the backend
  // canView rule). Enrol busts the module cache (updateTag in the action), so
  // router.refresh() re-renders with canView=true and the media uri served.
  // Mirrors CourseStartCard's free-enrol path.
  function doEnroll() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    setError(null);
    startTransition(async () => {
      const r = await enroll(moduleId);
      if (r.ok) {
        router.refresh();
      } else if (r.code === "MODULE_NOT_ENROLLABLE") {
        // Subscription mode → plans; single mode → per-item checkout.
        // (Same branch as the other start cards. /profil/narocnine does exist on a
        // single-payment portal, but with no subscriptionPlans it renders the
        // FreePortalNotice explainer — nothing purchasable — so checkout is the
        // only destination that can actually unlock the module.)
        router.push(langPath(lang, isSubscription ? "/profil/narocnine" : `/checkout/${moduleId}`));
      } else {
        setError(r.error ?? null);
      }
    });
  }

  function toggleLike() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !liked;
    setLiked(next);
    startTransition(async () => {
      const r = await likeModule(moduleId, next);
      if (!r.ok) {
        setLiked(!next);
        setError(r.error ?? null);
      }
    });
  }

  function toggleSave() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !saved;
    setSaved(next);
    startTransition(async () => {
      const r = await saveModule(moduleId, next);
      if (!r.ok) {
        setSaved(!next);
        setError(r.error ?? null);
      }
    });
  }

  const priceCents = price != null && Number(price) > 0 ? Number(price) : null;

  // Editorial gold pill — matches dashboard / CourseStartCard CTAs.
  const ctaClass =
    "inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-accent text-accent-foreground font-display font-bold uppercase tracking-[0.12em] text-[12px] md:text-[13px] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 active:brightness-100";
  const ctaStyle = {
    boxShadow:
      "0 2px 4px rgb(0 0 0 / 0.10), 0 8px 18px -6px color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 45%) 45%, transparent), inset 0 1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), white 80%) 70%, transparent), inset 0 -1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 40%) 20%, transparent)",
  };

  const disabledCtaClass =
    "inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-accent/40 text-primary/70 font-display font-bold uppercase tracking-[0.12em] text-[12px] md:text-[13px] cursor-not-allowed";

  // CTA matrix — mirrors CourseStartCard:
  //   canView           → play CTA (or hidden when the player is shown inline)
  //   free (not viewable) → free enrol ("Start for free") — €0, one tap
  //   paid              → buy / subscribe / unavailable
  // The `free` branch is what CourseStartCard has and this sidebar was missing:
  // without it, free OTT/podcasts fell straight to "Currently unavailable".
  let cta: React.ReactNode = null;
  if (canView) {
    if (!hidePlayCta) {
      cta = (
        <a href={playAnchor} className={ctaClass} style={ctaStyle}>
          <PlayCircle className="size-4" aria-hidden />
          {playLabel ?? t.detail.watchNow}
        </a>
      );
    }
  } else if (free) {
    // Free still requires an account on this portal (single-purchase gating:
    // canView stays false until enrolled). For a GUEST, promising "Click to
    // listen/watch" contradicts the auth-gate lock on the player — so show the
    // same "Log in" CTA instead. Once authed, the button free-enrols (€0) and
    // the refresh flips canView, revealing the real play CTA.
    cta = !isAuthed ? (
      <button
        type="button"
        onClick={() => router.push(loginUrl())}
        className={ctaClass}
        style={ctaStyle}
      >
        <LogIn className="size-4" aria-hidden />
        {t.detail.logInCta}
      </button>
    ) : (
      <button
        type="button"
        onClick={doEnroll}
        disabled={pending}
        className={cn(ctaClass, "disabled:pointer-events-none disabled:opacity-60")}
        style={ctaStyle}
      >
        <PlayCircle className="size-4" aria-hidden />
        {pending ? "…" : (freeLabel ?? t.detail.clickToOpen)}
      </button>
    );
  } else {
    const lock = paidLockKind(price, isSubscription);
    if (lock === "buy") {
      cta = (
        <Link href={langPath(lang, `/checkout/${moduleId}`)} className={ctaClass} style={ctaStyle}>
          <Lock className="size-4" aria-hidden />
          {pt.payperBuyLabel}
        </Link>
      );
    } else if (lock === "subscribe") {
      cta = (
        <Link href={langPath(lang, "/profil/narocnine")} className={ctaClass} style={ctaStyle}>
          <Lock className="size-4" aria-hidden />
          {subscribeLabel ?? t.detail.subscribe}
        </Link>
      );
    } else if (!isAuthed) {
      // No unlock path for an authed viewer (single mode, not free, no price),
      // but a guest can't know their entitlement until they log in — offer
      // login rather than a dead-end disabled button.
      cta = (
        <button
          type="button"
          onClick={() => {
            router.push(loginUrl());
          }}
          className={ctaClass}
          style={ctaStyle}
        >
          <LogIn className="size-4" aria-hidden />
          {t.detail.logInCta}
        </button>
      );
    } else {
      cta = (
        <button type="button" disabled className={disabledCtaClass}>
          <Lock className="size-4" aria-hidden />
          {pt.gatedUnavailable}
        </button>
      );
    }
  }

  // Like/Save come in two shapes so this shared card matches CourseStartCard
  // when a primary CTA is present (compact icon circle to the CTA's right),
  // yet still degrades to labelled full-width buttons when the CTA is
  // suppressed (podcast — the player is rendered inline, so `cta` is null).
  const circleBtn =
    "grid size-12 shrink-0 place-items-center rounded-full border transition-colors";
  const circleOn = "border-accent/30 bg-accent/15 text-accent";
  const circleOff =
    "border-border/60 text-foreground/55 hover:bg-foreground/[0.04] hover:text-foreground";
  const wideBtn =
    "flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-full text-[12px] font-display font-bold uppercase tracking-[0.12em] transition-colors";
  const wideOn = "bg-accent/15 text-foreground";
  const wideOff =
    "border border-border/60 text-foreground/75 hover:bg-foreground/[0.04] hover:text-foreground";

  const likeCircle = (
    <button
      type="button"
      onClick={toggleLike}
      aria-pressed={liked}
      aria-label={liked ? t.detail.liked : t.detail.like}
      title={liked ? t.detail.liked : t.detail.like}
      className={cn(circleBtn, liked ? circleOn : circleOff)}
    >
      <Heart className={cn("size-5", liked ? "fill-current" : "")} aria-hidden />
    </button>
  );

  const saveCircle = showSave ? (
    <button
      type="button"
      onClick={toggleSave}
      aria-pressed={saved}
      aria-label={saved ? t.detail.saved : t.detail.save}
      title={saved ? t.detail.saved : t.detail.save}
      className={cn(circleBtn, saved ? circleOn : circleOff)}
    >
      {saved ? (
        <BookmarkCheck className="size-5" aria-hidden />
      ) : (
        <Bookmark className="size-5" aria-hidden />
      )}
    </button>
  ) : null;

  const likeWide = (
    <button
      type="button"
      onClick={toggleLike}
      aria-pressed={liked}
      className={cn(wideBtn, liked ? wideOn : wideOff)}
    >
      <Heart className={cn("size-3.5", liked ? "fill-current text-accent" : "")} aria-hidden />
      {liked ? t.detail.liked : t.detail.like}
    </button>
  );

  const saveWide = showSave ? (
    <button
      type="button"
      onClick={toggleSave}
      aria-pressed={saved}
      className={cn(wideBtn, saved ? wideOn : wideOff)}
    >
      {saved ? (
        <BookmarkCheck className="size-3.5 text-accent" aria-hidden />
      ) : (
        <Bookmark className="size-3.5" aria-hidden />
      )}
      {saved ? t.detail.saved : t.detail.save}
    </button>
  ) : null;

  return (
    // Sticky lives on the column wrapper in the parent detail client (so
    // any future sibling cards pin together with this one), not the inner
    // aside — see CourseStartCard for the same pattern.
    <aside className="media-sidebar">
      <div className="bg-card text-card-foreground rounded-card border border-border/60 p-6 md:p-7 overflow-hidden">
        {/* Price block — prominent, mirrors CourseStartCard's price treatment. */}
        <div className="media-sidebar__price">
          {free ? (
            <p className="font-display text-[22px] font-extrabold text-accent">{t.detail.free}</p>
          ) : priceCents ? (
            <>
              <p className="font-display text-[26px] font-extrabold text-foreground tabular-nums tracking-[-0.01em]">
                {formatPrice(priceCents)}
              </p>
              <p className="mt-0.5 text-[11.5px] text-foreground/55">{t.detail.oneTimeLifetime}</p>
            </>
          ) : isSubscription ? (
            <p className="font-display text-[16px] font-bold text-foreground">
              {t.detail.membersOnly}
            </p>
          ) : (
            <p className="font-display text-[12.5px] font-bold uppercase tracking-[0.14em] text-foreground/55">
              {pt.gatedUnavailable}
            </p>
          )}
        </div>

        {/* Auth-gate note: whenever a signed-out visitor hits a gate, explain it
            and offer register alongside the login CTA below — the shared gate
            copy (portal.authGateBodyTpl) with {login}/{register} link slots,
            the same template PaywallCard uses. */}
        {!canView && !isAuthed ? (
          <p className="mt-3 text-[12.5px] leading-relaxed text-foreground/70">
            {pt.authGateBodyTpl.split(/(\{login\}|\{register\})/).map((part, i) =>
              part === "{login}" ? (
                <Link
                  key={i}
                  href={langPath(lang, "/login")}
                  className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {pt.authGateLoginWord}
                </Link>
              ) : part === "{register}" ? (
                <Link
                  key={i}
                  href={langPath(lang, "/register")}
                  className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {pt.authGateRegisterWord}
                </Link>
              ) : (
                part
              ),
            )}
          </p>
        ) : null}

        {/* CTA + Like — Like sits as a compact circle to the right of the
            primary CTA (CourseStartCard layout). When the play CTA is
            suppressed (hidePlayCta), the actions keep the full-width form. */}
        {cta ? (
          <div className="mt-4 flex items-center gap-2">
            <div className="min-w-0 flex-1">{cta}</div>
            {likeCircle}
            {saveCircle}
            {share ? (
              <ShareActions
                variant="circle"
                url={share.url}
                title={share.title}
                text={share.text}
              />
            ) : null}
          </div>
        ) : (
          <div className={cn("mt-4 grid gap-2", showSave ? "grid-cols-2" : "grid-cols-1")}>
            {likeWide}
            {saveWide}
          </div>
        )}

        {rows.length > 0 ? (
          <dl className="mt-6 grid gap-2.5 text-[12.5px]">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <r.Icon className="size-3.5 text-foreground/40 shrink-0" aria-hidden />
                <dt className="text-foreground/60 flex-1">{r.label}</dt>
                <dd className="font-display font-semibold text-foreground tabular-nums">
                  {r.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {error ? <p className="text-xs text-destructive mt-3">{error}</p> : null}
      </div>
    </aside>
  );
}
